import { useEffect, useRef, useState } from 'react';
import { usePromptModels } from '../../hooks/usePromptModels';
import type { CastingActor, CastingSelectionState } from '../../state/useGenerationState';
import type { Scenario } from '../types';
import {
  planCastingHydrationOnScenarioChange,
  promptModelToCastingActor,
  resolveCastingHydrationOutcome,
  shouldApplyCastingHydrationResult,
} from '../utils';

interface UseCastingHydrationParams {
  activeScenarioId: string | null;
  scenarios: Scenario[];
  selectedCasting: CastingActor | null;
  additionalCastings: CastingActor[];
  setSelectedCasting: (casting: CastingActor | null) => void;
  setAdditionalCastings: (castings: CastingActor[]) => void;
  castingSelectionState: CastingSelectionState;
  setCastingSelectionState: (state: CastingSelectionState) => void;
  /** ?model= ディープリンクで積んだキャストの適用対象シナリオ pending 状態
   *  (settingsContext.tsx。修正2-a「修正1との相互作用」精緻化)。 */
  pendingDeepLinkCastScenarioId: string | null;
  /** preserve/hydrate を確定した際に pending を消費 (クリア) する。 */
  consumePendingDeepLinkCast: () => void;
}

/**
 * シナリオ DB キャスト (scenarios.casting_model_ids) の FE 復元
 * (docs/features/character-casting-consistency-plan.md 修正2-a)。
 *
 * アクティブシナリオが変わったとき、casting_model_ids を正本として
 * usePromptModels({ ids }) で解決し、selectedCasting / additionalCastings へ順序を
 * 保って復元する。例外は ?model= ディープリンク直後の初回シナリオ選択のみ
 * (下記「修正1 (ディープリンク) との相互作用」参照)。
 *
 * hydration ガード (計画書 修正2-a 参照) の6ルールをすべて満たす:
 *   1. シナリオIDごとに初回1回だけ (handledScenarioIdRef)
 *   2. 同一シナリオIDで explicit ならクエリ再取得でも上書きしない
 *   3. シナリオIDが変わったときのみクリア+inherited hydration (pending-deep-link 保持は除く)
 *   4. 古いシナリオのクエリが遅延完了しても現在のシナリオに適用しない
 *   5. 取得結果を casting_model_ids 順に並べ直す
 *   6. 一部俳優が解決できない場合も解決できた俳優の順序を保持し console.warn を残す
 *
 * 修正1 (ディープリンク) との相互作用 (2026-08-20 実装レビュー P1×2 で精緻化): ローカル
 * キャストを保持する preserve は「pendingDeepLinkCastScenarioId が PENDING_NEXT_SCENARIO
 * または今回の activeScenarioId と一致していて、かつローカルキャストがある」場合のみに
 * 限定する。boolean の一回限りフラグ (旧 hasPendingDeepLinkCast) では「どのシナリオに
 * 適用する pending か」を表現できず、シナリオ表示中 (hydration 済み) に ?model= が処理
 * されると消費されないまま残り、次のシナリオ切替で持ち越されるバグがあったため、適用対象
 * シナリオIDで管理する。単なる castingSelectionState==='explicit' 判定で preserve すると、
 * シナリオ A で通常編集したキャストが B へ持ち越されてしまうため (受け入れ条件違反)、以前の
 * 状態が explicit でも pending が今回のシナリオに一致しなければ必ず新シナリオの DB 値で
 * hydrate する。preserve/hydrate いずれを確定した場合も、残っている pending はその時点で
 * 消費 (クリア) する (対象と異なるシナリオへ切り替えた時点で pending は無効になるため)。
 */
export function useCastingHydration(params: UseCastingHydrationParams) {
  const {
    activeScenarioId, scenarios,
    selectedCasting, additionalCastings, setSelectedCasting, setAdditionalCastings,
    castingSelectionState, setCastingSelectionState,
    pendingDeepLinkCastScenarioId, consumePendingDeepLinkCast,
  } = params;

  // 直近に hydration 処理 (hydrate または preserve) を確定させたシナリオID。
  const handledScenarioIdRef = useRef<string | null>(null);
  // 現在 casting_model_ids の解決クエリを待っているシナリオID (完了/破棄で null に戻す)。
  const [hydratingScenarioId, setHydratingScenarioId] = useState<string | null>(null);
  const [hydratingModelIds, setHydratingModelIds] = useState<string[]>([]);

  // 非同期完了時に最新の castingSelectionState を同期的に読むための ref
  // (ルール2: クエリ待機中にユーザーが編集して explicit になった場合の検知)。
  const castingSelectionStateRef = useRef(castingSelectionState);
  useEffect(() => {
    castingSelectionStateRef.current = castingSelectionState;
  }, [castingSelectionState]);

  // アクティブシナリオの切替検知 (ルール1・3、修正1との相互作用)。
  useEffect(() => {
    if (!activeScenarioId) return; // setup へ戻る場合は何もしない (現行キャストを維持)
    if (handledScenarioIdRef.current === activeScenarioId) return; // 既に処理済み

    const hasLocalCast = !!selectedCasting || additionalCastings.length > 0;
    const action = planCastingHydrationOnScenarioChange({
      activeScenarioId,
      handledScenarioId: handledScenarioIdRef.current,
      hasLocalCast,
      pendingDeepLinkCastScenarioId,
    });
    if (action === 'noop') return;

    if (action === 'preserve') {
      // pending-deep-link 由来のキャスト (?model= ディープリンク) を保持し、DB 復元は
      // しない。pending をここで消費する (次回以降のシナリオ切替は必ず hydrate)。
      handledScenarioIdRef.current = activeScenarioId;
      setHydratingScenarioId(null);
      consumePendingDeepLinkCast();
      return;
    }

    // action === 'hydrate': scenario 本体がまだ来ていなければ次回の再取得を待つ
    // (ref は更新しない = 未処理のまま。scenarios が populate されたら再実行される)。
    const scenario = scenarios.find((s) => s.id === activeScenarioId);
    if (!scenario) return;

    handledScenarioIdRef.current = activeScenarioId;
    setSelectedCasting(null);
    setAdditionalCastings([]);
    setCastingSelectionState('inherited');
    // hydrate を確定した時点で pending-deep-link が残っていれば消費する。対象と異なる
    // シナリオへ hydrate した = その pending はもう有効な適用先を持たないため (残すと
    // 後続のシナリオ切替で誤って preserve が発動してしまう)。
    if (pendingDeepLinkCastScenarioId !== null) {
      consumePendingDeepLinkCast();
    }

    const ids = scenario.casting_model_ids ?? [];
    if (ids.length === 0) {
      // 復元対象なし。即完了扱い (生成ガードをブロックしない)。
      setHydratingScenarioId(null);
      setHydratingModelIds([]);
      return;
    }
    setHydratingModelIds(ids);
    setHydratingScenarioId(activeScenarioId);
    // selectedCasting/additionalCastings/castingSelectionState/pendingDeepLinkCastScenarioId
    // は「新しいシナリオIDに切り替わった瞬間」の値だけを見ればよく、変化のたびに再評価する
    // 必要はない (handledScenarioIdRef の早期 return で1シナリオ1回に制御済み)。既存の同種
    // effect (ScenarioGenerator.tsx の behaviorRestoredScenarioIdRef 等) と同じ流儀。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScenarioId, scenarios]);

  // casting_model_ids 解決クエリ (修正1と同じ ids 専用クエリ)。
  const hydrationQuery = usePromptModels(
    hydratingScenarioId ? { ids: hydratingModelIds } : {},
    { enabled: hydratingScenarioId !== null },
  );

  // クエリ完了時の適用 (ルール2・4・5・6)。
  useEffect(() => {
    if (!hydratingScenarioId) return;
    const requestedScenarioId = hydratingScenarioId;

    const outcome = resolveCastingHydrationOutcome(hydratingModelIds, {
      data: hydrationQuery.data,
      isError: hydrationQuery.isError,
    });
    if (outcome.kind === 'loading') return;

    if (outcome.kind === 'error') {
      console.warn(`[useCastingHydration] キャスト復元クエリが失敗しました (scenario=${requestedScenarioId})`);
      setHydratingScenarioId(null);
      return;
    }
    if (outcome.kind === 'no-cast') {
      setHydratingScenarioId(null);
      return;
    }

    if (!shouldApplyCastingHydrationResult({
      requestedScenarioId,
      activeScenarioId,
      castingSelectionState: castingSelectionStateRef.current,
    })) {
      // 古いシナリオへの遅延適用 (ルール4)、または待機中に explicit へ編集された (ルール2)。
      setHydratingScenarioId(null);
      return;
    }

    if (outcome.missingIds.length > 0) {
      console.warn(
        `[useCastingHydration] 一部のキャスト俳優を解決できませんでした (scenario=${requestedScenarioId}, missing=${outcome.missingIds.join(', ')})`,
      );
    }

    const actors = outcome.actors.map(promptModelToCastingActor);
    const [primary, ...additional] = actors;
    setSelectedCasting(primary ?? null);
    setAdditionalCastings(additional);
    setCastingSelectionState('inherited');
    setHydratingScenarioId(null);
  }, [
    hydrationQuery.data, hydrationQuery.isError, hydratingScenarioId, hydratingModelIds,
    activeScenarioId, setSelectedCasting, setAdditionalCastings, setCastingSelectionState,
  ]);

  return {
    /** casting_model_ids の解決クエリが in-flight (アクティブシナリオ分)。
     *  復元完了前の動画生成ガードに使う。 */
    isCastingHydrating: hydratingScenarioId !== null && hydratingScenarioId === activeScenarioId,
  };
}
